import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, BadgeCheck } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { membershipCopy } from '@/lib/membershipValue';
import { track } from '@/lib/tracking';

/**
 * Orange conversion band. The price pill now shows BOTH the single-lead price
 * and the member per-lead price (derived in membershipValue.ts), and the
 * secondary button reads "Compare plans" instead of the generic "Pricing".
 */
const CtaBand: React.FC = () => {
  const { t, path, lang } = useI18n();
  const { profile } = useAuth();
  const copy = membershipCopy(lang);

  return (
    <section className="relative overflow-hidden bg-gradient-to-r from-brand-orange via-orange-500 to-orange-600">
      <div
        className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full bg-white/15 blur-3xl"
        aria-hidden="true"
      />
      <div className="relative mx-auto flex max-w-7xl flex-col gap-6 px-4 py-10 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:px-8">
        <div className="max-w-2xl">
          <h2 className="text-2xl font-extrabold tracking-tight text-white sm:text-3xl">
            {t('home.bandTitle')}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-orange-50 sm:text-base">{t('home.bandSub')}</p>
          <p className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-semibold text-white ring-1 ring-white/25">
            <BadgeCheck className="h-4 w-4 shrink-0" />
            {copy.bandPrice}
          </p>
        </div>
        <div className="flex shrink-0 flex-col gap-3 sm:flex-row">
          <Link
            to={profile ? path('/dashboard') : `${path('/register')}?role=subcontractor`}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-white px-6 py-4 text-base font-bold text-orange-600 shadow-lg transition-all duration-200 hover:-translate-y-0.5 hover:bg-orange-50"
          >
            {profile ? t('nav.dashboard') : t('home.bandCta')} <ArrowRight className="h-5 w-5" />
          </Link>
          <Link
            to={path('/pricing')}
            onClick={() => track('cta_click', { action: 'home_band_compare_plans' })}
            className="inline-flex items-center justify-center rounded-xl border border-white/40 px-6 py-4 text-base font-bold text-white transition-all duration-200 hover:-translate-y-0.5 hover:bg-white/10"
          >
            {copy.bandSecondary}
          </Link>
        </div>
      </div>
    </section>
  );
};

export default CtaBand;
